/** Magic-link / token TTLs */
export const ACCESS_TOKEN_TTL_SECONDS = 15 * 60; // 15 min
export const REFRESH_TOKEN_TTL_SECONDS = 30 * 24 * 60 * 60; // 30 days
export const VENDOR_SESSION_TTL_SECONDS = 24 * 60 * 60; // 24 h
export const MAGIC_LINK_TTL_SECONDS = 24 * 60 * 60; // 24 h
export const PASSWORD_RESET_TTL_SECONDS = 60 * 60; // 1 h
export const ACCOUNT_INVITATION_TTL_SECONDS = 7 * 24 * 60 * 60; // 7 days
export const SUPER_ADMIN_HANDOFF_TTL_SECONDS = 60; // 60 s

/** Password rules */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;
export const BCRYPT_ROUNDS = 12;

/** TOTP */
export const TOTP_ISSUER = 'SiteWise';
export const TOTP_WINDOW = 1; // accept ±1 step (~30s drift)

/** Storage */
export const DEFAULT_STORAGE_QUOTA_BYTES = 50 * 1024 * 1024 * 1024; // 50 GB
export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB per file in Phase 0
export const PRESIGN_UPLOAD_TTL_SECONDS = 10 * 60;
export const PRESIGN_DOWNLOAD_TTL_SECONDS = 5 * 60;

/** Image compression target (client-side, browser-image-compression) */
export const IMAGE_MAX_EDGE_PX = 1920;
export const IMAGE_JPEG_QUALITY = 0.85;

/** Slugs that may not be used as org subdomains because they collide with
 *  platform infrastructure or look-alike public names. */
export const RESERVED_ORG_SLUGS = [
  'admin',
  'api',
  'app',
  'auth',
  'docs',
  'help',
  'mail',
  'public',
  'static',
  'status',
  'support',
  'www',
] as const;

/** Notification categories (keep in sync with the Postgres enum) */
export const NOTIFICATION_CATEGORIES = [
  'work_order',
  'violation',
  'compliance',
  'inspection',
  'vendor',
  'reminder',
  'system',
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

/** Notification urgency (keep in sync with the `notification_urgency` enum). */
export const NOTIFICATION_URGENCIES = ['urgent', 'normal', 'low'] as const;
export type NotificationUrgency = (typeof NOTIFICATION_URGENCIES)[number];

/** Notification channels (keep in sync with the `notification_channel` enum). */
export const NOTIFICATION_CHANNELS = ['in_app', 'email', 'sms'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

/** Per-channel delivery status (keep in sync with `notification_delivery_status`). */
export const NOTIFICATION_DELIVERY_STATUSES = [
  'pending',
  'sent',
  'delivered',
  'failed',
  'suppressed',
] as const;
export type NotificationDeliveryStatus = (typeof NOTIFICATION_DELIVERY_STATUSES)[number];

/** Reasons for a notification to be suppressed before delivery. */
export const NOTIFICATION_SUPPRESSED_REASONS = [
  'user_unsubscribed',
  'quiet_hours',
  'digest',
  'quota',
  'no_address',
  'invalid_address',
  'bounced_previously',
  'sms_stopped',
  'feature_disabled',
  'unverified_phone',
] as const;
export type NotificationSuppressedReason = (typeof NOTIFICATION_SUPPRESSED_REASONS)[number];

/** Per-user digest frequency. `realtime` is the default. */
export const DIGEST_FREQUENCIES = ['realtime', 'daily', 'weekly'] as const;
export type DigestFrequency = (typeof DIGEST_FREQUENCIES)[number];

/** Where an unsubscribe row originated. */
export const UNSUBSCRIBE_SOURCES = ['settings_ui', 'email_link', 'compliance_request'] as const;
export type UnsubscribeSource = (typeof UNSUBSCRIBE_SOURCES)[number];

/** Phone-verification limits. */
export const PHONE_VERIFICATION_TTL_SECONDS = 10 * 60; // 10 minutes
export const PHONE_VERIFICATION_MAX_ATTEMPTS = 5;
export const PHONE_VERIFICATION_CODE_LENGTH = 6;

/** SMS rate-limits per recipient (rolling Redis counters). */
export const SMS_RATE_LIMIT_PER_HOUR = 5;
export const SMS_RATE_LIMIT_PER_DAY = 20;
/** Append the "Reply STOP" disclosure on the first SMS to a phone in
 *  any 30-day window; honored by the SMS sender. */
export const SMS_DISCLOSURE_WINDOW_DAYS = 30;

/** Default daily 8am send time (org-local) for digest emails. */
export const DIGEST_DAILY_HOUR_LOCAL = 8;
/** Monday is the default weekly digest day (ISO 1). */
export const DIGEST_WEEKLY_ISO_DAY = 1;

/** Unsubscribe-link signed-JWT TTL. */
export const UNSUBSCRIBE_TOKEN_TTL_SECONDS = 90 * 24 * 60 * 60;

/** Email bounce suppression window (after a hard bounce, hold all sends). */
export const EMAIL_BOUNCE_SUPPRESSION_DAYS = 30;

/** Magic-link purposes (keep in sync with the Postgres enum) */
export const MAGIC_LINK_PURPOSES = [
  'vendor_signin',
  'password_reset',
  'email_verification',
  'account_invitation',
] as const;
export type MagicLinkPurpose = (typeof MAGIC_LINK_PURPOSES)[number];

/** Audit-log actor kinds (keep in sync with the Postgres enum) */
export const AUDIT_ACTOR_KINDS = ['user', 'system', 'super_admin'] as const;
export type AuditActorKind = (typeof AUDIT_ACTOR_KINDS)[number];

/** Rate-limit defaults (per-IP, per-route group) */
export const RATE_LIMITS = {
  authLogin: { windowMs: 15 * 60 * 1000, max: 20 },
  authMagicLinkRequest: { windowMs: 60 * 60 * 1000, max: 5 },
  /** Vendor magic-link: 20 requests per IP per hour (email limit reuses authMagicLinkRequest). */
  vendorMagicLinkIp: { windowMs: 60 * 60 * 1000, max: 20 },
  authPasswordReset: { windowMs: 60 * 60 * 1000, max: 5 },
  uploadsPresign: { windowMs: 60 * 1000, max: 30 },
  importsCreate: { windowMs: 60 * 60 * 1000, max: 10 },
  searchQuick: { windowMs: 60 * 1000, max: 240 },
  geocode: { windowMs: 60 * 1000, max: 60 },
  /** Google Places proxy: roughly one autocomplete keystroke per ~200ms,
   *  plus the details fetch on selection. 240/min gives ~4 active sessions
   *  worth of comfort headroom while still capping abuse. */
  placesAutocomplete: { windowMs: 60 * 1000, max: 240 },
  default: { windowMs: 60 * 1000, max: 120 },
} as const;

/** NYC GeoSearch cache TTL — successful resolutions stay fresh for 90 days. */
export const GEOCODE_CACHE_TTL_DAYS = 90;
export const GEOCODE_CACHE_TTL_MS = GEOCODE_CACHE_TTL_DAYS * 24 * 60 * 60 * 1000;
/** Single-call timeout for the GeoSearch HTTP request (ms). */
export const GEOCODE_REQUEST_TIMEOUT_MS = 8_000;

/** Hard limits for the CSV/XLSX importer. The worker enforces these
 *  before it parses; the API rejects oversized uploads at presign. */
export const MAX_IMPORT_BYTES = 10 * 1024 * 1024; // 10 MB
export const MAX_IMPORT_ROWS = 50_000;
/** Rows committed per DB transaction during the import commit phase. */
export const IMPORT_COMMIT_BATCH_SIZE = 100;

/** NYC borough enum values (lowercased canonical form). Used by the
 *  buildings schema and surfaced from the geocoder. */
export const BOROUGHS = [
  'manhattan',
  'bronx',
  'brooklyn',
  'queens',
  'staten_island',
] as const;
export type Borough = (typeof BOROUGHS)[number];

export const BOROUGH_LABELS: Record<Borough, string> = {
  manhattan: 'Manhattan',
  bronx: 'Bronx',
  brooklyn: 'Brooklyn',
  queens: 'Queens',
  staten_island: 'Staten Island',
};

/** NYC GeoSearch maps borough strings differently (full name, sometimes
 *  capitalised); normalize through this helper. Returns null if no match. */
export function normalizeBorough(input: string | null | undefined): Borough | null {
  if (!input) return null;
  const key = input.toLowerCase().replace(/\s+/g, '_');
  return (BOROUGHS as readonly string[]).includes(key) ? (key as Borough) : null;
}

export const UNIT_TYPES = [
  'apartment',
  'room',
  'suite',
  'common_area',
  'mechanical',
  'other',
] as const;
export type UnitType = (typeof UNIT_TYPES)[number];

export const OCCUPANCY_STATUSES = ['occupied', 'vacant', 'out_of_service', 'unknown'] as const;
export type OccupancyStatus = (typeof OCCUPANCY_STATUSES)[number];

export const GEOCODE_SOURCES = ['nyc_geosearch', 'manual'] as const;
export type GeocodeSource = (typeof GEOCODE_SOURCES)[number];

export const IMPORT_KINDS = ['buildings_csv', 'buildings_xlsx'] as const;
export type ImportKind = (typeof IMPORT_KINDS)[number];

// ---------- Migration 1: Unit Hub ----------
// Utility, appliance, document, custom-field, and lead-status enums
// for the new unit detail page. Kept in lock-step with the Postgres
// enums declared in `0009_unit_hub.sql`.

export const HEAT_SOURCES = [
  'radiator_steam',
  'radiator_hot_water',
  'forced_air',
  'electric_baseboard',
  'heat_pump',
  'other',
  'none',
] as const;
export type HeatSource = (typeof HEAT_SOURCES)[number];

export const HOT_WATER_SOURCES = [
  'gas_tank',
  'electric_tank',
  'gas_tankless',
  'electric_tankless',
  'building_central',
  'other',
] as const;
export type HotWaterSource = (typeof HOT_WATER_SOURCES)[number];

export const STOVE_FUELS = [
  'gas',
  'electric_coil',
  'electric_induction',
  'electric_glass',
  'none',
] as const;
export type StoveFuel = (typeof STOVE_FUELS)[number];

export const UNIT_DOCUMENT_KINDS = [
  'lease',
  'photo',
  'floor_plan',
  'manual',
  'inspection_report',
  'permit',
  // ---- Migration 2: lead-tracker document kinds ----
  // Added so the unit Lead tab can attach an XRF report PDF, an
  // exemption-approval letter, and arbitrary work-evidence files
  // (receipts, photos, certificates) without needing a new owner kind.
  'lead_xrf_report',
  'lead_exemption_letter',
  'lead_work_evidence',
  'other',
] as const;
export type UnitDocumentKind = (typeof UNIT_DOCUMENT_KINDS)[number];

/** Building Records filing-cabinet categories (certificates / permits). */
export const BUILDING_DOCUMENT_CATEGORIES = [
  'boiler',
  'elevator',
  'fire_alarm',
  'sprinkler',
  'rpz',
  'standpipe',
  'permit',
  'other',
] as const;
export type BuildingDocumentCategory = (typeof BUILDING_DOCUMENT_CATEGORIES)[number];

export const BUILDING_DOCUMENT_CATEGORY_LABELS: Record<BuildingDocumentCategory, string> = {
  boiler: 'Boiler',
  elevator: 'Elevator',
  fire_alarm: 'Fire alarm',
  sprinkler: 'Sprinkler',
  rpz: 'RPZ',
  standpipe: 'Standpipe',
  permit: 'Permits',
  other: 'Other',
};

export const UNIT_CUSTOM_FIELD_TYPES = [
  'text',
  'long_text',
  'number',
  'date',
  'yes_no',
  'single_select',
  'multi_select',
] as const;
export type UnitCustomFieldType = (typeof UNIT_CUSTOM_FIELD_TYPES)[number];

/** Lead-status column values. The next migration is responsible for
 *  populating this on existing units; this migration only declares the
 *  enum + the column so the unit detail page can render its pill. */
export const LEAD_STATUSES = [
  'untested',
  'tested_negative',
  'tested_positive',
  'exempt_approved',
  'exempt_pending',
  'exempt_rejected',
  'friction_clean_only',
  'unknown',
] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

export const IMPORT_STATUSES = [
  'pending',
  'previewing',
  'previewed',
  'committing',
  'completed',
  'cancelled',
  'failed',
] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];
